import axios from "axios";
import { api } from "./api_route";

// Case Primary Information Routes --> Asset Details (Bank Account)

// =========== GET ALL BANK ACCOUNTS BY CASE ID ===========
export const getAllBankAccountsByCaseId = async (caseDetailsID) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBankAccountDetails/all/${caseDetailsID}`
        );

        return {
            success: true,
            data: response.data.data || [],
            assetOwnedBy: response.data.assetOwnedBy || '',
            count: response.data.count || 0,
            message: response.data.message || "Bank accounts fetched successfully",
        };
    } catch (error) {
        console.error("Error fetching bank accounts:", error);
        return {
            success: false,
            data: [],
            count: 0,
            message: error.response?.data?.message || "Failed to fetch bank accounts",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== GET SINGLE BANK ACCOUNT BY ID ===========
export const getBankAccountById = async (bankAccountID) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBankAccountDetails/${bankAccountID}`
        );

        return {
            success: true,
            data: response.data.data,
            parties: response.data.parties,
            // message: response.data.message || "Bank account fetched successfully",
        };
    } catch (error) {
        console.error("Error fetching bank account:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to fetch bank account",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== CREATE NEW BANK ACCOUNT ===========
export const createBankAccount = async (bankAccountData) => {
    try {
        const response = await axios.post(
            `${api}/case/caseBankAccountDetails`,
            bankAccountData
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Bank account created successfully",
        };
    } catch (error) {
        console.error("Error creating bank account:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to create bank account",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE BANK ACCOUNT ===========
export const updateBankAccount = async (bankAccountID, bankAccountData) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBankAccountDetails/${bankAccountID}`,
            bankAccountData
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Bank account updated successfully",
        };
    } catch (error) {
        console.error("Error updating bank account:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update bank account",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== DELETE BANK ACCOUNT ===========
export const deleteBankAccount = async (bankAccountID, updatedBy) => {
    try {
        const response = await axios.delete(
            `${api}/case/caseBankAccountDetails/${bankAccountID}`,
            { data: { updatedBy } }
        );

        return {
            success: true,
            message: response.data.message || "Bank account deleted successfully",
        };
    } catch (error) {
        console.error("Error deleting bank account:", error);
        return {
            success: false,
            message: error.response?.data?.message || "Failed to delete bank account",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== SEARCH BANK ACCOUNTS ===========
export const searchBankAccounts = async (caseDetailsID, searchCriteria) => {
    try {
        const response = await axios.post(
            `${api}/case/caseBankAccountDetails/search/${caseDetailsID}`,
            searchCriteria
        );

        return {
            success: true,
            data: response.data.data || [],
            count: response.data.count || 0,
            message: response.data.message || "Search completed successfully",
        };
    } catch (error) {
        console.error("Error searching bank accounts:", error);
        return {
            success: false,
            data: [],
            count: 0,
            message: error.response?.data?.message || "Failed to search bank accounts",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== BULK CREATE BANK ACCOUNTS ===========
export const bulkCreateBankAccounts = async (bankAccountsData) => {
    try {
        const response = await axios.post(
            `${api}/case/caseBankAccountDetails/bulk`,
            { bankAccounts: bankAccountsData }
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Bank accounts created successfully",
        };
    } catch (error) {
        console.error("Error creating bulk bank accounts:", error);
        return {
            success: false,
            data: [],
            message: error.response?.data?.message || "Failed to create bank accounts",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== GET BANK ACCOUNTS WITH PAGINATION ===========
export const getBankAccountsWithPagination = async (caseDetailsID, page = 1, limit = 10) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBankAccountDetails/paginated/${caseDetailsID}`,
            { params: { page, limit } }
        );

        return {
            success: true,
            data: response.data.data || [],
            pagination: response.data.pagination || {},
            message: response.data.message || "Bank accounts fetched successfully",
        };
    } catch (error) {
        console.error("Error fetching bank accounts with pagination:", error);
        return {
            success: false,
            data: [],
            pagination: {},
            message: error.response?.data?.message || "Failed to fetch bank accounts",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== EXPORT BANK ACCOUNTS TO EXCEL ===========
export const exportBankAccountsToExcel = async (caseDetailsID) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBankAccountDetails/export/${caseDetailsID}`,
            { responseType: "blob" }
        );

        return {
            success: true,
            data: response.data,
            message: "Export successful",
        };
    } catch (error) {
        console.error("Error exporting bank accounts:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to export bank accounts",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== HELPER FUNCTIONS ===========


export const formatBankAccountData = (data) => {
    const formatted = { ...data };

    if (Array.isArray(formatted.bankAttachment)) {
        formatted.bankAttachment = formatted.bankAttachment.join(",");
    }

    if (Array.isArray(formatted.cancelledChequeAttachment)) {
        formatted.cancelledChequeAttachment = formatted.cancelledChequeAttachment.join(",");
    }

    if (formatted.caseDetailsID && !formatted.caseRowID) {
        formatted.caseRowID = formatted.caseDetailsID;
    }

    return formatted;
};


export const parseAttachmentIds = (attachmentField) => {
    if (!attachmentField) {
        return [];
    }
    if (Array.isArray(attachmentField)) {
        return attachmentField;
    }
    if (typeof attachmentField !== 'string') {
        return [];
    }
    if (attachmentField.trim() === "") {
        return [];
    }
    return attachmentField
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "");
};


export const validateBankAccountData = (data) => {
    const errors = {};

    if (!data.caseDetailsID) {
        errors.caseDetailsID = "Case ID is required";
    }

    if (!data.assetClassification) {
        errors.assetClassification = "Asset Classification is required";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};


export const bankFilesDownload = async (values) => {
    try {
        const response = await axios.get(`${api}/documents/asset/bankAccount/${values}`, {
            responseType: "blob",
        });

        return response.data;
    } catch (error) {
        return [];
    }
};
